import { useMemo, useState } from "react";
import { Route, Navigation, Phone, ChevronLeft, ChevronRight, Clock, MapPin, ExternalLink } from "lucide-react";

const addDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const longDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

// UK postcode → sortable key: area letters, district number, district letter, sector, unit.
export function postcodeKey(pc = "") {
  const m = pc.toUpperCase().replace(/\s+/g, "").match(/^([A-Z]{1,2})(\d{1,2})([A-Z]?)(?:(\d)([A-Z]{2}))?$/);
  if (!m) return ["ZZ", 99, "", 9, pc.toUpperCase()];
  return [m[1], parseInt(m[2], 10), m[3], m[4] ? parseInt(m[4], 10) : 9, m[5] || ""];
}
const cmpKey = (a, b) => { for (let i = 0; i < a.length; i++) { if (a[i] < b[i]) return -1; if (a[i] > b[i]) return 1; } return 0; };

export const stopAddress = (v) => {
  const addr = (v.address || "").trim();
  const pc = (v.postcode || "").trim();
  if (!addr) return pc;
  return pc && !addr.toUpperCase().includes(pc.toUpperCase()) ? `${addr}, ${pc}` : addr;
};
const mapsTo = (v) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(stopAddress(v))}&travelmode=driving`;
export const fullRouteUrl = (stops) => {
  if (!stops.length) return "";
  const dest = encodeURIComponent(stopAddress(stops[stops.length - 1]));
  const wps = stops.slice(0, -1).map(s => encodeURIComponent(stopAddress(s))).join("%7C");
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}${wps ? `&waypoints=${wps}` : ""}&travelmode=driving`;
};

export function defaultRouteDate(events, today) {
  const dates = events.filter(e => e.type === "site_visit" && e.date >= today).map(e => e.date).sort();
  return dates[0] || today;
}

// Day route — that day's site visits in postcode (or time) order with one-tap Google Maps links.
export function DayRoutePlanner({ events, today, date, onChangeDate, onOpenBooking }) {
  const [order, setOrder] = useState("postcode");
  const stops = useMemo(() => {
    const day = events.filter(e => e.type === "site_visit" && e.date === date);
    return order === "postcode"
      ? [...day].sort((a, b) => cmpKey(postcodeKey(a.postcode), postcodeKey(b.postcode)) || (a.time || "").localeCompare(b.time || ""))
      : [...day].sort((a, b) => (a.time || "99:99").localeCompare(b.time || "99:99"));
  }, [events, date, order]);
  if (!date) return null;
  const isToday = date === today;
  return (
    <section data-testid="day-route-planner" className="mb-8 border border-[#E5E7EB] bg-white">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-[#E5E7EB] bg-[#12263A] text-white flex-wrap">
        <div className="text-[10px] uppercase tracking-widest font-black inline-flex items-center gap-2">
          <Route size={13} className="text-[#C79A3B]" /> Day route
        </div>
        <div className="inline-flex items-center gap-1">
          <button type="button" data-testid="day-route-prev" onClick={() => onChangeDate(addDays(date, -1))} className="p-1.5 hover:bg-white/10" aria-label="Previous day"><ChevronLeft size={14} /></button>
          <button type="button" data-testid="day-route-today" onClick={() => onChangeDate(today)} className={`px-2.5 py-1 text-xs font-bold ${isToday ? "text-[#C79A3B]" : "hover:bg-white/10"}`}>Today</button>
          <button type="button" data-testid="day-route-next" onClick={() => onChangeDate(addDays(date, 1))} className="p-1.5 hover:bg-white/10" aria-label="Next day"><ChevronRight size={14} /></button>
        </div>
      </div>
      <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap border-b border-[#F0EAE0]">
        <div>
          <div className="font-black text-[#12263A]" data-testid="day-route-date">{isToday ? "Today · " : ""}{longDate(date)}</div>
          <div className="text-xs text-[#6B7280]" data-testid="day-route-count">{stops.length === 0 ? "No site visits" : `${stops.length} ${stops.length === 1 ? "visit" : "visits"} · ${order === "postcode" ? "postcode order" : "appointment order"}`}</div>
        </div>
        <div className="inline-flex border border-[#E5E7EB] text-[10px] font-black uppercase tracking-widest">
          {[["postcode", "Postcode"], ["time", "Time"]].map(([v, l]) => (
            <button key={v} type="button" data-testid={`day-route-order-${v}`} onClick={() => setOrder(v)}
              className={`px-3 py-1.5 ${order === v ? "bg-[#12263A] text-white" : "bg-white text-[#6B7280] hover:bg-[#FAFAFA]"}`}>{l}</button>
          ))}
        </div>
      </div>
      {stops.length === 0 ? (
        <div className="px-4 py-6 text-sm text-[#9CA3AF] inline-flex items-center gap-2"><MapPin size={14} /> Nothing to drive to on this day.</div>
      ) : (
        <>
          <ol className="divide-y divide-[#F0EAE0]">
            {stops.map((v, i) => <RouteStop key={`${v.reference}-${v.time}`} v={v} n={i + 1} onOpen={onOpenBooking} />)}
          </ol>
          <div className="px-4 py-3 bg-[#FAFAFA] border-t border-[#E5E7EB]">
            <a href={fullRouteUrl(stops)} target="_blank" rel="noopener noreferrer" data-testid="day-route-open-maps"
              className="inline-flex items-center gap-2 bg-[#C79A3B] hover:bg-[#B1872F] text-[#12263A] px-4 py-2.5 text-xs font-black uppercase tracking-widest transition-colors">
              <Navigation size={14} /> Open full route in Google Maps <ExternalLink size={12} />
            </a>
            <div className="text-[11px] text-[#6B7280] mt-1.5">Starts from wherever your phone is · {stops.length} {stops.length === 1 ? "stop" : "stops"} in order</div>
          </div>
        </>
      )}
    </section>
  );
}

function RouteStop({ v, n, onOpen }) {
  const tel = (v.phone || "").replace(/\s+/g, "");
  return (
    <li data-testid={`day-route-stop-${v.reference}`} className="px-4 py-3 flex items-start gap-3">
      <div className="w-7 h-7 shrink-0 rounded-full bg-[#5B2A83] text-white text-xs font-black grid place-items-center" data-testid={`day-route-stop-no-${v.reference}`}>{n}</div>
      <button type="button" onClick={() => onOpen?.(v.reference)} className="flex-1 min-w-0 text-left">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 text-xs font-black text-[#5B2A83] bg-[#EADBF3] px-1.5 py-0.5"><Clock size={11} /> {v.time || "TBC"}</span>
          <span className="font-bold text-[#12263A] truncate">{v.client_name || v.reference}</span>
          <span className="text-[10px] font-black uppercase tracking-widest text-[#6B7280] border border-[#E5E7EB] px-1 py-0.5" data-testid={`day-route-stop-pc-${v.reference}`}>{v.postcode || "no postcode"}</span>
        </div>
        <div className="text-xs text-[#6B7280] truncate">{v.address || "address TBC"}</div>
      </button>
      <div className="flex items-center gap-1.5 shrink-0">
        {tel && <a href={`tel:${tel}`} data-testid={`day-route-call-${v.reference}`} title={v.phone} className="p-2 border border-[#E5E7EB] hover:bg-[#FAFAFA] text-[#12263A]" aria-label="Call client"><Phone size={14} /></a>}
        <a href={mapsTo(v)} target="_blank" rel="noopener noreferrer" data-testid={`day-route-nav-${v.reference}`}
          className="inline-flex items-center gap-1.5 bg-[#12263A] hover:bg-[#1B3550] text-white px-3 py-2 text-[10px] font-black uppercase tracking-widest transition-colors">
          <Navigation size={12} /> Navigate
        </a>
      </div>
    </li>
  );
}
